import * as THREE from "three";
import { ALUMINUM_MATERIAL_PARAMS } from "./AluminumMaterial";
import { heightMapToNormalMap } from "./proceduralNormalMap";

/**
 * Procedural ribbed surface for Kantenschutz (angle sections). The cross-section
 * is mostly flat with narrow ridges at a regular spacing (roughly
 * `___/'\___/'\___`). Ridges run parallel to the extrusion axis.
 *
 * Baked into a normal map and scaled to physical millimetres via `texture.repeat`.
 */

// One texture tile spans this many ridges; combined with the spacing this fixes
// the physical size of a tile so callers only need `repeat = faceMm / TILE_MM`.
const RIDGES_PER_TILE = 10;
const RIDGE_SPACING_MM = 9;
const TILE_MM = RIDGES_PER_TILE * RIDGE_SPACING_MM;

const TEXTURE_SIZE = 256;

/** Which UV axis the ridges vary along. */
type RidgeAxis = "u" | "v";

type RidgeTextureConfig = Readonly<{
  axis: RidgeAxis;
  /** Fraction of a ridge period for one flank; smaller → narrower grooves. */
  ridgeHalfWidth: number;
  /** Height-map gradient strength when baking the normal map. */
  normalIntensity: number;
  /** Material normalScale multiplier applied in the shader. */
  normalScale: number;
}>;

// Kantenschutz: larger flat faces — pattern can read a bit stronger.
const ANGLE_SECTION_RIDGE_CONFIG: RidgeTextureConfig = {
  axis: "v",
  ridgeHalfWidth: 0.14,
  normalIntensity: 1.15,
  normalScale: 1,
};

const textureCache = new Map<string, THREE.CanvasTexture>();

/**
 * Height profile of a single ridge period: a narrow raised ridge centred on the
 * period boundary, flat in between (`___/'\___`).
 */
function ridgeHeight(t: number, ridgeHalfWidth: number): number {
  const phase = (t * RIDGES_PER_TILE) % 1;
  const distanceToRidge = Math.min(phase, 1 - phase);
  if (distanceToRidge >= ridgeHalfWidth) {
    return 0;
  }
  return 1 - distanceToRidge / ridgeHalfWidth;
}

function buildRidgeHeightMap(
  size: number,
  axis: RidgeAxis,
  ridgeHalfWidth: number,
): Float32Array {
  const data = new Float32Array(size * size);
  for (let i = 0; i < size; i++) {
    const height = ridgeHeight(i / size, ridgeHalfWidth);
    for (let j = 0; j < size; j++) {
      const index = axis === "u" ? j * size + i : i * size + j;
      data[index] = height;
    }
  }
  return data;
}

function getRidgeNormalMap(config: RidgeTextureConfig): THREE.CanvasTexture {
  const key = `alu-ridge-${config.axis}-${config.ridgeHalfWidth}-${config.normalIntensity}`;
  const cached = textureCache.get(key);
  if (cached) {
    return cached.clone();
  }

  const heightMap = buildRidgeHeightMap(
    TEXTURE_SIZE,
    config.axis,
    config.ridgeHalfWidth,
  );
  const normalMap = heightMapToNormalMap(
    TEXTURE_SIZE,
    heightMap,
    config.normalIntensity,
  );

  textureCache.set(key, normalMap);
  return normalMap.clone();
}

function createRibbedMaterial(
  normalMap: THREE.CanvasTexture,
  normalScale: number,
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    ...ALUMINUM_MATERIAL_PARAMS,
    normalMap,
    normalScale: new THREE.Vector2(normalScale, normalScale),
  });
}

/**
 * Ribbed aluminium for Kantenschutz (`AngleSectionRenderer`). The legs are
 * `ExtrudeGeometry` faces whose top UVs are already in millimetres, with the
 * length along U and the leg depth along V. The parts are extruded along their
 * length, so the ridges run along U and vary along the depth (V).
 */
export function getAngleSectionAluminumMaterial(): THREE.MeshStandardMaterial {
  const normalMap = getRidgeNormalMap(ANGLE_SECTION_RIDGE_CONFIG);
  normalMap.repeat.set(1, 1 / TILE_MM);
  return createRibbedMaterial(
    normalMap,
    ANGLE_SECTION_RIDGE_CONFIG.normalScale,
  );
}
